import React, { useState } from 'react'
import { Card, Form, Input, Button, Typography, Alert, Space, Divider } from 'antd'
import { LockOutlined, UserOutlined, MailOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'

const { Title, Text } = Typography

export const Login: React.FC = () => {
  const navigate = useNavigate()
  const { login, portalConfig } = useCustomerAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [magicLinkLoading, setMagicLinkLoading] = useState(false)

  const handleLogin = async (values: any) => {
    try {
      setLoading(true)
      setError(null)

      const response = await customerApi.login(values.email, values.password)

      await login(response.token)
      const defaultLandingPage = portalConfig?.defaultLandingPage || '/dashboard'
      navigate(defaultLandingPage, { replace: true })

    } catch (err: any) {
      console.error('Login error:', err)
      if (err.response?.status === 401) {
        const errorMessage = err.response?.data?.message
        if (errorMessage?.includes('magic link') || errorMessage?.includes('password first')) {
          setError('You need to set up your password first. Please use the "Request Access Link" option below.')
        } else {
          setError('Invalid email or password. Please try again.')
        }
      } else {
        setError(err.response?.data?.message || 'Login failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRequestMagicLink = async (values: any) => {
    try {
      setMagicLinkLoading(true)
      setError(null)

      await customerApi.requestMagicLink({
        email: values.email
      })

      setMagicLinkSent(true)

    } catch (err: any) {
      console.error('Magic link error:', err)
      setError(err.response?.data?.message || 'Failed to send magic link. Please try again.')
    } finally {
      setMagicLinkLoading(false)
    }
  }

  if (magicLinkSent) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '16px'
      }}>
        <Card style={{ width: '100%', maxWidth: 400, textAlign: 'center', borderRadius: '12px' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <MailOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            <Title level={3}>Check Your Email</Title>
            <Text type="secondary">
              We've sent you a magic link to access your customer portal. 
              Click the link in your email to log in instantly.
            </Text>
            <Button 
              type="link" 
              onClick={() => setMagicLinkSent(false)}
            >
              Back to Login
            </Button>
          </Space>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '16px'
    }}>
      <Card style={{ width: '100%', maxWidth: 400, borderRadius: '12px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2}>Customer Portal</Title>
          <Text type="secondary">
            Access your service requests, invoices, and more
          </Text>
        </div>

        {error && (
          <Alert
            message="Login Failed"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Form
          name="login"
          onFinish={handleLogin}
          layout="vertical"
          size="large"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="your.email@example.com"
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Please enter your password' }
            ]}
            style={{ marginBottom: '24px' }}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Your password"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Link to="/forgot-password">
            Forgot your password?
          </Link>
        </div>

        <Divider>Or</Divider>

        <Form
          name="magic-link"
          onFinish={handleRequestMagicLink}
          layout="vertical"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Enter your email for magic link"
            />
          </Form.Item>

          <Form.Item>
            <Button
              htmlType="submit"
              loading={magicLinkLoading}
              block
              size="large"
            >
              Send Magic Link
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            New customer? Your business will send you a welcome link to get started.
          </Text>
        </div>
      </Card>
    </div>
  )
}
